import { useState, useCallback, useEffect, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import debounce from "lodash/debounce";

import TextInput from "@stadium/ui/src/components/form/TextInput";
import Label from "@stadium/ui/src/components/form/Label";
import Multimedia from "@sharedComponents/multimedia";
import { getCountryFlagUrl } from "@stadium/ui/src/shared/country-flag";
import { ExternalButton } from "@sharedComponents/buttons";
import { useConfig } from "@stadium/hooks/lib/use-config";

import AccordionAllCollapsable from "@stadium/ui/src/components/accordion/accordion-all-collapsable";

import { allowPositiveIntegerValueOnlyValidator } from "@stadium/ui/src/components/form/TextInput/helpers";
import { DIGITS_PATTERN } from "@stadium/helpers/form/constants";
import { US_CURRENCY_CODE } from "@constants/store";
import { isCustomCurrency } from "@stadium/helpers/currency";

import { useStoreContext } from "@components/store/provider";

import CartProductItems from "@sharedComponents/cart/send-to-many/cart-product-items";
import SendToOthersItemName from "@components/store/cart/send-to-many/send-to-others-item-name";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import { pluralizeWord } from "@stadium/helpers/string";

import { convertPrice, formatPrice } from "@helpers/currency";

import GlobalRedemptionInfoTip from "@sharedComponents/cart/global-redemption-info-tip";

import {
  getCartOrderRecipientZones,
  getCartProductZoneList,
  getCartOrderCurrency,
} from "@selectors/cart";
import { getIsHideCountryFlag, getStoreCurrencyCode } from "@selectors/store";
import {
  getCountriesGroupedByZoneUsingCountryZoneId,
  getConversionRates,
  getGlobalCountryZoneId,
} from "@stadium/redux-utils/selectors/configSelector";

import { useCartListingContext } from "@contexts/cart-listing-context";
import { getMenuRoute } from "@constants/routes";
import { GLOBAL_COUNTRY } from "@constants/country";

import styles from "../main.module.scss";
import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";

const emptyObj = {};
const emptyArr = [];

const NUMBER_OF_RECIPIENTS_CHAR_LIMIT = 8;

const CartListingWrapper = (dataProps) => {
  const recipientZones = useSelector(getCartOrderRecipientZones);
  const cartProductZoneList = useSelector(getCartProductZoneList);
  const conversionRates = useConfig(getConversionRates);
  const storeCurrencyCode = useSelector(getStoreCurrencyCode);
  const orderCurrencyCode = useSelector(getCartOrderCurrency);
  const hideCountryFlag = useSelector(getIsHideCountryFlag);
  const { getUpdatedStoreBasePath } = useStoreContext();

  const globalCountryZoneId = useConfig(getGlobalCountryZoneId);

  const currencyCode = useMemo(
    () => orderCurrencyCode || storeCurrencyCode,
    [orderCurrencyCode, storeCurrencyCode],
  );

  const { storeDisplayCurrencyCode } = useStoreContext();

  const { formatPriceInDisplayStoreCurrency } = useStoreCurrencyConverter();

  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const isProductFromSingleCountry = cartProductZoneList.length === 1;

  const { productListData = emptyObj } = dataProps;

  const {
    lineItemsData = emptyArr,
    productSetsData = emptyArr,
    medleySetsData = emptyArr,
    zoneId = "",
  } = productListData;

  const countriesGroupedByZone = useConfig(getCountriesGroupedByZoneUsingCountryZoneId);
  const selectedZoneCountry = countriesGroupedByZone?.[zoneId?.toString?.()]?.[0];
  const isGlobalZoneSelected = zoneId === globalCountryZoneId;
  const selectedCountry = selectedZoneCountry || GLOBAL_COUNTRY;

  const getInitialRecipientCount = () => {
    const selectedRecipientsDetail = recipientZones.find(
      ({ zoneId: recipientZoneId }) => recipientZoneId === +zoneId,
    );
    return selectedRecipientsDetail?.count || 1;
  };
  const [numberOfRecipients, setNumberOfRecipients] = useState(getInitialRecipientCount);
  const [totalPricePerRecipient, setTotalPricePerRecipient] = useState(0);
  const [numberOfRecipientsError, setNumberOfRecipientsError] = useState(false);
  const [accordionExpandedId, setAccordionExpandedId] = useState(null);
  const isOpen = zoneId === accordionExpandedId;
  const { setSendToOthersZoneWiseRecipient } = useCartListingContext();

  const noOfRecipientChangeHandler = useCallback(
    debounce((value, zoneId) => {
      setSendToOthersZoneWiseRecipient((prevArray) => [
        ...prevArray.filter(({ zoneId: selectedZoneId }) => selectedZoneId !== +zoneId),
        { zoneId: +zoneId, recipientCount: value },
      ]);
    }, 1000),
    [],
  );

  useEffect(() => {
    noOfRecipientChangeHandler(numberOfRecipients, zoneId);
  }, [numberOfRecipients, zoneId]);

  const getTotalPricePerRecipient = (value) => setTotalPricePerRecipient(value);

  const storeBasePath = getUpdatedStoreBasePath({
    countryCode: selectedCountry?.iso?.toLowerCase(),
  });

  const numberOfRecipientsChangeHandler = ({ value }) => {
    setNumberOfRecipients(value);
    +value ? setNumberOfRecipientsError(false) : setNumberOfRecipientsError(true);
  };

  const renderContinueBtn = () => (
    <ExternalButton
      href={getMenuRoute({ storeBasePath })}
      styleType="link"
      className={styles.continueBtn}
    >
      {isMobileDevice ? "keep shopping" : "Continue shopping"}
    </ExternalButton>
  );

  const renderCountryHeader = () => {
    const itemsCount = lineItemsData.length + productSetsData.length + medleySetsData.length;
    const countryFlagUrl = getCountryFlagUrl({ iso: selectedCountry?.iso }, "round");
    return (
      <div className={styles.countryFlagRow}>
        {hideCountryFlag ? (
          <span className={`${styles.countryIso} ${styles.flagImageText}`}>
            {selectedCountry?.iso}
          </span>
        ) : (
          <div className={styles.flagImage}>
            <Multimedia
              alt={selectedCountry?.name}
              type="image"
              src={countryFlagUrl}
              width={24}
              height={24}
              className={""}
            />
          </div>
        )}
        <div className={styles.flagImageText}>
          <span>
            {isGlobalZoneSelected ? "Global " : ""}
            recipients (
            {`${itemsCount} ${pluralizeWord("item", {
              count: itemsCount,
            })}`}
            )
          </span>
          <GlobalRedemptionInfoTip
            isGlobalZoneSelected={isGlobalZoneSelected}
            iconInfoClass={styles.iconInfo}
            learnMoreClass={styles.learnMore}
            toolTipWrapperClass={styles.tooltipWrapperWithFlagImage}
          />
        </div>
      </div>
    );
  };

  const renderMobileCountryHeader = () => {
    return (
      <div className={styles.accordionHeader}>
        {renderCountryHeader()}
        <div className={styles.collapseIconBlock}>
          <span className={`${styles.arrowIcon} ${isOpen ? styles.iconUp : styles.iconDown}`} />
        </div>
      </div>
    );
  };

  const renderDesktopHeader = () => (
    <div className={styles.countryFlagCtaRow}>
      {renderCountryHeader()}
      {renderContinueBtn()}
    </div>
  );

  const totalPricePerRecipientInDefaultCurrency = convertPrice({
    from: currencyCode,
    to: US_CURRENCY_CODE,
    value: totalPricePerRecipient,
    conversionRates,
  });

  const totalPricePerRecipientInDisplayCurrency = isCustomCurrency(storeDisplayCurrencyCode)
    ? formatPrice(totalPricePerRecipientInDefaultCurrency, {
        currencyDisplay: "code",
        currencyCode: US_CURRENCY_CODE,
      })
    : formatPriceInDisplayStoreCurrency(totalPricePerRecipientInDefaultCurrency, US_CURRENCY_CODE);

  const renderRecipientsInputField = () => (
    <div className={styles.inputFieldCostRow}>
      <TextInput
        id="noOfRecipients"
        label={
          <Label htmlFor="noOfRecipients" showIcon={false} className={styles.labelName}>
            {isGlobalZoneSelected
              ? "Number of global recipients"
              : "Number of Recipients in this country"}
          </Label>
        }
        value={numberOfRecipients}
        onChange={numberOfRecipientsChangeHandler}
        maxLength={NUMBER_OF_RECIPIENTS_CHAR_LIMIT}
        validate={allowPositiveIntegerValueOnlyValidator}
        pattern={DIGITS_PATTERN}
        error={numberOfRecipientsError}
        inputMode="numeric"
        className={styles.inputTextField}
      />
      {numberOfRecipientsError && (
        <div className={`${styles.errorMsgText}`}>At least 1 recipient required</div>
      )}
      <div className={styles.costText}>
        Item cost per recipient:{" "}
        <span className={styles.priceText}>{totalPricePerRecipientInDisplayCurrency}</span>
      </div>
    </div>
  );

  const renderBodyDesktopView = () => (
    <>
      {renderDesktopHeader()}
      <div className={styles.cardRowList}>
        <CartProductItems {...dataProps} getTotalPricePerRecipient={getTotalPricePerRecipient} />
      </div>
      <div className={styles.inputOptionalGroup}>
        {renderRecipientsInputField()}
        {isProductFromSingleCountry && (
          <div className={styles.fullWidth}>
            <SendToOthersItemName />
          </div>
        )}
      </div>
      {!isProductFromSingleCountry && <div className={styles.dividerBottom}></div>}
    </>
  );

  const renderBodyMobileView = () => {
    return (
      <AccordionAllCollapsable
        id={zoneId}
        eventKey={zoneId}
        toggleValue={zoneId}
        isOpen={isOpen}
        expandedId={accordionExpandedId}
        handleAccordianToggle={(id) => setAccordionExpandedId(id)}
        titleContent={renderMobileCountryHeader()}
        accordionCustomClass={`${styles.accordion} ${styles.accordionItemsList}`}
        bodyContent={
          <>
            {renderContinueBtn()}
            <CartProductItems
              {...dataProps}
              getTotalPricePerRecipient={getTotalPricePerRecipient}
            />
            {renderRecipientsInputField()}
          </>
        }
      />
    );
  };

  return isMobileDevice ? renderBodyMobileView() : renderBodyDesktopView();
};
export default CartListingWrapper;
